import { Period } from '@vy/types';
import { buildPeriod, newUUID, vyDate } from '@vy/utils';
import { getBarChartColors } from '../../../../../../utils/charts/bar-chart/get-bar-chart-colors';
import { PeriodsData } from './ContractPhasesTimelineContainer';
import { ContractPhasesDataRowPlacementContainer } from './ContractPhasesDataRowPlacementContainer';
import { BAR_HORIZONTAL_OFFSET, MIN_BAR_WIDTH } from './contract-phases-timeline-consts';

export type RenderBarProps = {
  color: string;
  width: number;
  height: number;
  periodData: PeriodsData;
};

type ContractPhasesDataRowsProps = {
  columnWidth: number;
  columnAmount: number;
  fullPeriod: Period;
  phasesData: PeriodsData[];
  barHeight: number;
  barsGap?: number;
  renderBar?: (props: RenderBarProps) => React.ReactNode;
};

export const ContractPhasesDataRows: React.FC<ContractPhasesDataRowsProps> = ({
  columnWidth,
  columnAmount,
  fullPeriod,
  phasesData,
  barHeight,
  barsGap = 10,
  renderBar,
}) => {
  const basePlacementStartingPoint = calculateBaseHorizontalStartPoint(fullPeriod, columnWidth);

  return (
    <>
      {phasesData.map((periodData, index) => {
        const { period } = periodData;
        const prePhasePeriod = buildPeriod(fullPeriod.startTime, period.startTime);

        const phaseMonthAmount = vyDate(period.endTime).diff(vyDate(period.startTime), 'months', true);
        const phaseWidth = Math.max(MIN_BAR_WIDTH, phaseMonthAmount * columnWidth);

        return (
          <ContractPhasesDataRowPlacementContainer
            key={newUUID()}
            barHeight={barHeight}
            barsGap={barsGap}
            basePlacementStartingPoint={basePlacementStartingPoint}
            columnAmount={columnAmount}
            columnWidth={columnWidth}
            phaseIndex={index}
            prePhasePeriod={prePhasePeriod}
          >
            {(isDisplayingOffset) =>
              renderBar?.({
                color: getBarChartColors(index),
                width: calculateWidthWithOffset(phaseWidth, isDisplayingOffset),
                height: barHeight,
                periodData,
              })
            }
          </ContractPhasesDataRowPlacementContainer>
        );
      })}
    </>
  );
};

function calculateWidthWithOffset(phaseWidth: number, isDisplayingOffset: boolean): number {
  const offsetAmount = isDisplayingOffset ? -BAR_HORIZONTAL_OFFSET : 0;

  return phaseWidth + offsetAmount;
}

function calculateBaseHorizontalStartPoint(fullPeriod: Period, columnWidth: number): number {
  const fullPeriodStartDay = vyDate(fullPeriod.startTime).get('date') - 1;

  if (fullPeriodStartDay === 0) {
    return 0;
  }

  const daysInMonth = vyDate(fullPeriod.startTime).daysInMonth();
  const startPositionRatio = fullPeriodStartDay / daysInMonth;

  return startPositionRatio * columnWidth;
}
